import type { ContextBindingConfig } from '@wix/thunderbolt-becky-types'
import type { ContextProviderRegistry } from './types'

/**
 * Checks if the given value is a wix/signals-service signal
 */
const isSignal = (value: any) => {
	return typeof value?.peek === 'function' && typeof value?.get === 'function'
}

/** retrieve deeply nested value, unwrapping signals along the path */
const getSignalValueDeep = (target: any, path: string) => {
	let value = target
	for (const pathSegment of path.split('.')) {
		if (value) {
			const maybeSignal = value[pathSegment]
			value = isSignal(maybeSignal) ? maybeSignal.get() : maybeSignal
		}
	}
	return value
}

const cloneDeep = (obj: any): any => JSON.parse(JSON.stringify(obj))

/** this is a much simpler version of _.set since we don't need the extra complexity, and we want to max out render performance,
 * this method returns a deep copy of the original target object */
export const setDeep = (target: any, path: Array<string>, value: any) => {
	// replace the instance so react will re-render the component
	const root = cloneDeep(target)
	let curr = root
	for (let i = 0; i < path.length; i++) {
		if (i === path.length - 1) {
			const fieldName = path[i]
			curr[fieldName] = value
		} else {
			const pathSegment = path[i]
			if (!(pathSegment in curr)) {
				curr[pathSegment] = {}
			}
			curr = curr[pathSegment]
		}
	}
	return root
}

/** Retrieves the bound field value from the target context and set it under the binding prop name key */
export const getBoundProps = (
	bindings: Array<ContextBindingConfig>,
	contextProviderRegistry: ContextProviderRegistry
): { [prop: string]: unknown } => {
	return bindings.reduce<{ [prop: string]: unknown }>((agg, binding) => {
		const getContext = contextProviderRegistry.getUseContextHook(binding.contextProvider)
		const context = getContext()
		Object.entries(binding.properties).forEach(([compProp, contextFieldPath]) => {
			agg[compProp] = getSignalValueDeep(context, contextFieldPath)
		})
		return agg
	}, {})
}
